import Delay from '../../../../../common/scripts/Toolkit/Utils/Delay';
import { generateRandomString } from '../../../../../common/scripts/Toolkit/Utils/Utility';
import TableClient from './TableClient';
import { GamePathName } from './IGameService';
import SinglePlayGamePreviewData from '../DataObject/PreviewData/SinglePlayGamePreviewData';
import TableModel from '../Model/TableModel';
import CommonGamePreviewData from '../DataObject/PreviewData/CommonGamePreviewData';
import { PublishDataType } from './IClient';
import TableGame from '../../../System/TableGame';
import { PathName } from '../Config/PathConfig';
import DataType from '../Config/DataStructure';
import UserData from '../UserData';
import BettingModel from '../Model/BettingModel';
import Result from '../DataObject/Result';
import MultiplePlayGamePreviewData from '../DataObject/PreviewData/MultiplePlayGamePreviewData';
import RoundPlayGamePreviewData from '../DataObject/PreviewData/RoundPlayGamePreviewData';
import {
    ICommonOverviewData,
    OverviewDataType,
    MultiplePlayOverviewDataType,
    SinglePlayOverviewDataType,
    RoundPlayOverviewDataType,
} from '../Config/GamePreviewDataType';
import { GamePreviewData } from '../Model/HallModel';

export default class Client extends TableClient {
    // to ensure type safety, we need to override the emit method
    public emit<T extends keyof PublishDataType>(type: T, data: PublishDataType[T]): void {
        super.emit(type, data);
    }

    public async login() {
        const token = TableGame.config.token || generateRandomString(36);
        const { platformID, walletType } = TableGame.config;
        const response = await TableGame.connection.send(PathName.Login, {
            Token: token,
            PlatformsID: platformID,
            WalletTypesID: walletType,
            // Currency: currency,
        });
        return new UserData(response);
    }

    public async getAvatarList() {
        const response = await TableGame.connection.send(PathName.AvatarList);
        return response;
    }

    public async updateInfo(nickname: string, avatar: string) {
        const data: DataType.User.UserInfo = {
            Nickname: nickname,
            Avatar: avatar,
        };
        const response = await TableGame.connection.send(PathName.InfoUpdate, data);
        return response;
    }

    public async getEntryData(isCache: boolean = true) {
        if (this.entryData && isCache) {
            return this.entryData;
        }
        return Delay.event(GamePathName.Entry, this).then(() => this.entryData);
    }

    public async getInitData() {
        if (this.initData) {
            return this.initData;
        }
        return Delay.event(GamePathName.Init, this).then(() => this.initData);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public async getHistoryData(pageIndex: number, pageSize: number, startTime: number) {
        return null;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public async getHistoryDetailData(transactionID: string, roundID: number) {
        return null;
    }

    public getGamePreviewData(hallID: number, gameID: string, playType: DataType.Enums.PlayType, data: OverviewDataType): GamePreviewData {
        if (playType === DataType.Enums.PlayType.Single) {
            return this.instantiateSinglePlayGamePreviewData(hallID, gameID, playType, data as SinglePlayOverviewDataType);
        }
        if (playType === DataType.Enums.PlayType.Multiple) {
            return this.instantiateMultiplePlayGamePreviewData(hallID, gameID, playType, data as MultiplePlayOverviewDataType);
        }
        if (playType === DataType.Enums.PlayType.Round) {
            return this.instantiateRoundPlayGamePreviewData(hallID, gameID, playType, data as RoundPlayOverviewDataType);
        }
        return this.instantiateCommonGamePreviewData(hallID, gameID, playType, data as ICommonOverviewData);
    }

    public getTableModel(data: DataType.Table.SummaryData): TableModel {
        return this.instantiateTableModel(data);
    }

    public getResult(data: DataType.Table.PlayerSettleData): Result {
        return this.instantiateResult(data);
    }

    public getBettingModel(table: TableModel): BettingModel {
        return this.instantiateBettingModel(table);
    }

    protected instantiateSinglePlayGamePreviewData(hallID: number, gameID: string, playType: DataType.Enums.PlayType, data: SinglePlayOverviewDataType) {
        return new SinglePlayGamePreviewData(hallID, gameID, playType, data);
    }

    protected instantiateMultiplePlayGamePreviewData(hallID: number, gameID: string, playType: DataType.Enums.PlayType, data: MultiplePlayOverviewDataType) {
        return new MultiplePlayGamePreviewData(hallID, gameID, playType, data);
    }

    protected instantiateRoundPlayGamePreviewData(hallID: number, gameID: string, playType: DataType.Enums.PlayType, data: RoundPlayOverviewDataType) {
        return new RoundPlayGamePreviewData(hallID, gameID, playType, data);
    }

    protected instantiateCommonGamePreviewData(hallID: number, gameID: string, playType: DataType.Enums.PlayType, data: ICommonOverviewData) {
        return new CommonGamePreviewData(hallID, gameID, playType, data);
    }

    protected instantiateTableModel(data: DataType.Table.SummaryData) {
        return new TableModel(data, this);
    }

    protected instantiateBettingModel(table: TableModel) {
        return new BettingModel(table);
    }

    protected instantiateResult(data: DataType.Table.PlayerSettleData) {
        return new Result(data);
    }
}
